/**
 * LiteResult — the result phase: radar, top-three gaps, lead-capture form,
 * service link, trial unlock + ERIO add-on. Extracted verbatim from
 * LiteAssessment.jsx.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileDown } from "lucide-react";
import RadarMaturity from "../../components/charts/RadarMaturity";
import ErioAssessmentAddon from "../../components/ErioAssessmentAddon";
import ConfidenceBand from "../../components/ConfidenceBand";
import ToolkitDownload from "./ToolkitDownload";
import SectorBenchmark from "./SectorBenchmark";

export default function LiteResult({ lite, result, answers, radarSeries, lead, setLead, submitting, onSubmitLead, realSlug }) {
  return (
    <div data-testid="lite-result">
      <h2 className="ek-heading text-2xl md:text-3xl mb-2">Your {lite.sub_brand} maturity</h2>
      <p className="text-zinc-500 text-[13px] mb-8">Overall <strong className="text-[#7737F6] text-xl">{result.overall}/4</strong> — <strong>{result.level_label}</strong></p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase mb-3">Maturity radar</div>
          <RadarMaturity series={radarSeries} max={4} height={380} />
        </div>
        <div className="bg-white border border-zinc-200 p-5">
          <div className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase mb-3">Three things we'd talk about first</div>
          {result.top_three_gaps?.length ? (
            <ol className="space-y-3">
              {result.top_three_gaps.map((g, i) => (
                <li key={g.dimension_id} className="border-l-2 border-[#7737F6] pl-3" data-testid={`lite-gap-${g.dimension_id}`}>
                  <div className="text-[11px] font-medium tracking-[0.16em] text-[#7737F6] uppercase">{String(i+1).padStart(2,"0")}</div>
                  <div className="text-[14px] font-medium text-black">{g.label}</div>
                  <div className="text-[12px] text-zinc-600">Currently scoring {g.score}/4 — typically {g.score === 1 ? "two stages" : "one stage"} below where mid-market peers want to be.</div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-[14px] text-zinc-700">You're scoring Strategic across the board — congratulations. The full diagnostic looks for the next horizon: Optimising, AI-led service operations and continuous business-impact reporting.</p>
          )}
        </div>
      </div>

      {result.confidence && (
        <div className="mt-8">
          <ConfidenceBand confidence={result.confidence} sensitivity={result.sensitivity} />
          <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="confidence-cta">
            <Link
              to={`/trial/request${result?.session_id ? `?ls=${result.session_id}` : ""}`}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#7737F6] hover:underline"
              data-testid="confidence-cta-link"
            >
              Book a diagnostic to lift your verified-evidence backing from {Math.round((result.confidence.verified_pct || 0) * 100)}% and tighten this range
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      )}

      <ToolkitDownload slug={realSlug} answers={answers} subBrand={lite.sub_brand} />

      <SectorBenchmark slug={realSlug} overall={result.overall} subBrand={lite.sub_brand} />

      {/* Joined-up teaser — this lens is one of twelve in one method */}
      <div className="mt-10 border border-[#7737F6]/30 bg-[#7737F6]/5 p-6 md:p-7 rounded-lg" data-testid="lite-spine-teaser">
        <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7737F6] mb-2">This is one lens of twelve</div>
        <h3 className="ek-heading text-xl md:text-2xl text-black mb-2">See where {lite.sub_brand} sits in your whole estate.</h3>
        <p className="text-zinc-600 text-[14px] max-w-2xl mb-4">
          Your {lite.sub_brand} score plugs into a single <strong>0–100 Estate Maturity</strong> picture across all twelve EKSELENS360 lenses and your end-to-end value streams — and our engine tells you the
          single <strong>next-best assessment</strong> to close the biggest gap. One method, not thirteen tools.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/value-stream-assessment" className="ek-btn ek-btn-primary text-[13px]" data-testid="lite-teaser-vs-cta">
            Assess an end-to-end value stream <ArrowRight size={14} />
          </Link>
          <Link to="/methodology" className="ek-btn ek-btn-outline-dark text-[13px]" data-testid="lite-teaser-method-cta">
            How the 360 family works as one method
          </Link>
        </div>
      </div>

      {/* Lead capture form */}
      <div className="bg-[#0B0B0C] text-white mt-12 p-8 md:p-12" data-testid="lite-lead-form">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="text-[11px] font-medium tracking-[0.18em] text-[#A179FA] uppercase mb-3">Next step</div>
            <h3 className="ek-heading text-2xl md:text-3xl text-white">{lite.cta_headline}</h3>
            <p className="text-zinc-300 text-[14px] mt-4 font-light">{lite.cta_body}</p>
            <div className="text-[12.5px] text-zinc-400 mt-6 flex items-start gap-2">
              <FileDown size={14} className="mt-0.5 flex-shrink-0"/>
              <span>The full engagement produces a board-pack PDF, workshop deck, analyst workbook and improvement plan — all sector-calibrated against the Ekselens peer benchmark.</span>
            </div>
          </div>
          <form onSubmit={onSubmitLead} className="lg:col-span-3 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input required value={lead.full_name} onChange={(e) => setLead({ ...lead, full_name: e.target.value })} placeholder="Full name *" className="bg-white text-black px-3 py-2.5 text-[16px]" data-testid="lite-lead-name" />
              <input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Work email *" className="bg-white text-black px-3 py-2.5 text-[16px]" data-testid="lite-lead-email" />
              <input value={lead.organisation} onChange={(e) => setLead({ ...lead, organisation: e.target.value })} placeholder="Organisation" className="bg-white text-black px-3 py-2.5 text-[16px]" data-testid="lite-lead-org" />
              <input value={lead.role} onChange={(e) => setLead({ ...lead, role: e.target.value })} placeholder="Role" className="bg-white text-black px-3 py-2.5 text-[16px]" data-testid="lite-lead-role" />
              <select value={lead.sector} onChange={(e) => setLead({ ...lead, sector: e.target.value })} className="bg-white text-black px-3 py-2.5 text-[16px] md:col-span-2" data-testid="lite-lead-sector">
                <option value="">Sector (optional)</option>
                <option>Manufacturing</option>
                <option>Telecom</option>
                <option>Financial services</option>
                <option>Public sector</option>
                <option>Retail</option>
                <option>Healthcare</option>
                <option>Energy & utilities</option>
                <option>Professional services</option>
                <option>Other</option>
              </select>
            </div>
            <label className="flex items-start gap-2 text-[12px] text-zinc-300 cursor-pointer">
              <input type="checkbox" checked={lead.consent} onChange={(e) => setLead({ ...lead, consent: e.target.checked })} className="mt-0.5" data-testid="lite-lead-consent" />
              <span>I'd like Ekselens to follow up about a full {lite.sub_brand} diagnostic. I understand my email will be stored to enable this conversation.</span>
            </label>
            <button type="submit" disabled={submitting} className="ek-btn ek-btn-primary text-[14px] disabled:opacity-50" data-testid="lite-lead-submit">
              {submitting ? "Sending…" : "Book a discovery conversation"} <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>

      <div className="text-center mt-8">
        {lite.value_stream ? (
          <Link to="/enterprise360" className="text-[#7737F6] hover:underline text-[13px]" data-testid="lite-result-engine-link">
            See how the ENTERPRISE360 engine builds your blueprint →
          </Link>
        ) : (
          <Link to={`/services/${lite.service_slug}`} className="text-[#7737F6] hover:underline text-[13px]">
            Read about our {lite.sub_brand} service →
          </Link>
        )}
      </div>

      {!lite.value_stream && (
        <div className="mt-10 border-t border-zinc-200 pt-8 text-center">
          <div className="text-[11px] tracking-[0.16em] uppercase text-zinc-500 mb-2">Want the partner-grade lens on this?</div>
          <Link
            to={`/trial/request${result?.session_id ? `?ls=${result.session_id}` : ""}`}
            className="inline-flex items-center gap-2 text-[14px] font-medium text-[#7737F6] hover:underline"
            data-testid="lite-result-unlock-trial-cta"
          >
            Unlock the full {lite.sub_brand} assessment <ArrowRight size={14} />
          </Link>
          <div className="text-[11.5px] text-zinc-500 mt-2 max-w-md mx-auto">
            Partner-vetted, 7-day trial. Full indicator deep-dive, evidence
            reconciliation, peer benchmark, tailored CSI moves.
          </div>
        </div>
      )}

      <ErioAssessmentAddon
        answers={answers}
        subBrand={lite.sub_brand}
        overall={result.overall}
        slug={realSlug}
      />
    </div>
  );
}
